import MuiButton from '@mui/material/Button';
import { alpha, darken, type Theme } from '@mui/material/styles';
import { forwardRef, type AriaAttributes, type ReactNode, type Ref } from 'react';
import { isInternalPath } from '../../lib/isInternalPath';
import { fontWeights } from '../../theme';

export interface ButtonProps
  extends Pick<
    AriaAttributes,
    'aria-expanded' | 'aria-controls' | 'aria-haspopup' | 'aria-label'
  > {
  // `link` and `inline` are looks, not semantics: the control stays a real <button> (it acts on the
  // page, it navigates nowhere) and only takes a link's typography. For an actual destination pass
  // `href`, which renders an anchor — never either of these looks.
  variant?: 'contained' | 'outlined' | 'text' | 'link' | 'inline';
  color?: 'primary' | 'secondary' | 'error' | 'neutral';
  size?: 'small' | 'medium' | 'large';
  // Drops the theme's uppercase stamp and renders the label in the case it was written in, keeping
  // every other piece of button chrome. A curated escape hatch in the idiom of Text's `bold`, so a
  // view never reaches past the façade with an sx override — NOT a reversal of
  // `typography.button.textTransform`, which stays the sanctioned default (DESIGN.md §4): flipping
  // that restamps every button in the product and is ADR territory. Its callers are the ones whose
  // frames are drawn in sentence case: the address-filter dialog's footer, and the two quiet text
  // actions that used to be `variant="link"` — «Cambiar» in that dialog's header and «Editar» beside
  // the location tab's filter chips, neither of which sits in prose (SPEC amendment A5).
  sentenceCase?: boolean;
  // Bold label, in the idiom of `Text`'s own `bold` and of `sentenceCase` above: a curated escape
  // hatch, so a view never reaches past the façade with an sx override. MUI's `typography.button`
  // ships weight 500; its caller is the ficha's «Editar datos», which the design draws bold on the
  // status row. NOT a reversal of the theme's button weight, which stays the sanctioned default.
  bold?: boolean;
  loading?: boolean;
  disabled?: boolean;
  // Takes focus on mount. For a panel that REPLACES the control which opened it — otherwise the
  // keyboard user is dropped on <body> and has to re-traverse the region to reach the action.
  autoFocus?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  fullWidth?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  // A destination inside the panel: renders a real <a> in the same tab with a full reload, because
  // the façade does not depend on the router. External links go through IconButton, which forces
  // noopener; the type keeps them out of here.
  href?: `/${string}`;
  children: ReactNode;
}

// Capsule shape across every variant (contained/outlined/text): a large fixed radius
// clamps to a full pill at any size or width. Per-component brand shaping lives here,
// not in the global theme (docs/DESIGN.md §2).
const CAPSULE = { borderRadius: '9999px' } as const;

// Only the casing, so it composes with whichever look the button already has — unlike `linkSx`,
// which strips the chrome as well.
const SENTENCE_CASE = { textTransform: 'none' } as const;

// Only the weight, for the same reason `SENTENCE_CASE` is only the casing: it has to compose with
// whichever look the button already has. `fontWeights.bold` and not a literal 700 — the token is
// the one definition of what «bold» means in this product.
const BOLD = { fontWeight: fontWeights.bold } as const;

// The quiet action: an ink-on-white pill whose hairline is the ink token at the same
// 23% MUI uses for its own outlines, so it reads as a secondary control next to a
// purple primary without inventing a colour. MUI has no neutral role for Button, so
// it maps to `inherit` and the tokens are applied here (never a hardcoded hex).
const neutralSx = (theme: Theme) => ({
  ...CAPSULE,
  color: theme.palette.text.primary,
  borderColor: alpha(theme.palette.text.primary, 0.23),
  '&:hover': {
    borderColor: theme.palette.text.primary,
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
  },
});

// The sanction. `error.main` (#F26279) measures 3.09:1 on white — enough for a frame, never for
// colour that carries meaning in text — and on a button the tone is often the ONLY thing that
// separates a sanction from a quiet action sharing its size, weight and place on the row. `.dark`
// is 5.77:1 read either way (ink on white, white on the fill), and the theme derives it from
// `.main` on its own, so this adds no token and changes no API: every existing `color="error"`
// caller gets the contrast without being touched. Same rule and same reason as
// `Alert variant="filled"`, which already had to make this exact choice.
const errorSx = (variant: ButtonProps['variant']) => (theme: Theme) =>
  variant === 'contained'
    ? {
        ...CAPSULE,
        backgroundColor: theme.palette.error.dark,
        // Pinned rather than left to MUI's `contrastText`, which is derived from `main`: the white
        // label is half of the 5.77:1 this whole mapping exists for, and deriving it from a tone
        // the button no longer wears is how it would silently stop being white.
        color: theme.palette.common.white,
        '&:hover': { backgroundColor: darken(theme.palette.error.dark, 0.2) },
      }
    : {
        ...CAPSULE,
        color: theme.palette.error.dark,
        // The hairline keeps MUI's own 50% for an outlined border: it is decoration, and what has
        // to clear AA is the label above it, which does.
        borderColor: alpha(theme.palette.error.dark, 0.5),
        '&:hover': {
          borderColor: theme.palette.error.dark,
          backgroundColor: alpha(theme.palette.error.dark, 0.04),
        },
      };

// The action that reads as prose. Everything here undoes button chrome: the theme's uppercase
// label (a link is read as words, not as a stamped label), the capsule and its padding (there is
// no pill to sit in), and the minimum width MUI gives a button so short labels line up in a row.
// The underline is what carries the affordance once the chrome is gone, so it stays on at rest —
// an underline that only appears on hover leaves nothing to see for a keyboard or touch user.
const linkSx = {
  textTransform: 'none',
  textDecoration: 'underline',
  fontWeight: fontWeights.bold,
  borderRadius: 0,
  minWidth: 0,
  px: 0,
  '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
} as const;

// The link's typography MINUS the underline, for a control that hangs UNDER the prose it belongs to
// rather than sitting inside it: bold, sentence case, no chrome and no padding, so its first glyph
// lines up with the text above. Distinct from `link` on purpose — dropping the underline costs the
// affordance that variant keeps on at rest, so the two cannot be one look with a flag. It is only
// answerable where the frame around the control already says «this is actionable»: today the
// «Aviso de estado del buzón», whose «Ver entregas» sits under a message inside a bordered `Alert`.
// Contrast is not the concern (#7942ED on white is ~5,5:1, above the 4,5:1 required) — the signal is,
// which is why this is a curated variant and not something a view can reach for with an `sx`.
//
// Second caller and this becomes the shared quiet-action look; until then it is deliberately narrow.
const inlineSx = {
  textTransform: 'none',
  textDecoration: 'none',
  fontWeight: fontWeights.bold,
  borderRadius: 0,
  minWidth: 0,
  px: 0,
  '&:hover': { textDecoration: 'none', backgroundColor: 'transparent' },
} as const;

// forwardRef: the ref has to reach the rendered element — the <button>, or the <a> when `href` is
// set. Two callers need it, and they arrived independently — a menu anchors its popover to the
// element, and the marks banner returns focus to its trigger after a cancel. Mirrors IconButton
// and TextField.
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(
  {
    variant = 'contained',
    color = 'primary',
    size = 'medium',
    sentenceCase = false,
    bold = false,
    loading = false,
    disabled = false,
    autoFocus = false,
    fullWidth = false,
    startIcon,
    endIcon,
    onClick,
    href,
    type = 'button',
    children,
    // Disclosure aria (expand/collapse) plus what a menu trigger needs. An aria-label here must
    // CONTAIN the visible text, never replace it — the name is the label a speech user says.
    ...aria
  },
  ref,
) {
  // The link look wins over the capsule and over the neutral pill: it is the shape, not a tint.
  const lookSx =
    variant === 'link'
      ? linkSx
      : variant === 'inline'
        ? inlineSx
        : color === 'neutral'
          ? neutralSx
          : color === 'error'
            ? errorSx(variant)
            : CAPSULE;

  const variantProp = variant === 'link' || variant === 'inline' ? 'text' : variant;
  const colorProp = color === 'neutral' ? 'inherit' : color;
  // The casing and the weight are layered ON TOP of the look rather than folded into it: both are
  // orthogonal to all three, and `link` is sentence-case AND bold already, so a merge would
  // special-case the one look that needs neither. An array, so the modifiers compose in order and
  // either can be absent.
  const sx = [lookSx, ...(sentenceCase ? [SENTENCE_CASE] : []), ...(bold ? [BOLD] : [])];

  // Attribute lists duplicated on purpose, as in IconButton: MUI's polymorphic typing does not
  // survive a shared props object across `component="a"` and the default button.
  if (href !== undefined) {
    // A link that must lead nowhere (disabled, busy, or a path that would leave the panel) is
    // rendered disabled and without an href, so MUI marks it aria-disabled and takes it out of the
    // tab order. MUI gives an anchor without an href role="button"; the explicit role keeps it
    // announced as the link it is, dimmed, never as a button that does nothing.
    const dead = disabled || loading || !isInternalPath(href);
    return (
      <MuiButton
        // The union ref is this wrapper's public contract; each branch knows which element it renders.
        ref={ref as Ref<HTMLAnchorElement>}
        component="a"
        {...(dead ? { role: 'link' } : { href })}
        variant={variantProp}
        color={colorProp}
        size={size}
        loading={loading}
        disabled={dead}
        autoFocus={autoFocus}
        fullWidth={fullWidth}
        startIcon={startIcon}
        endIcon={endIcon}
        sx={sx}
        {...(onClick ? { onClick } : {})}
        {...aria}
      >
        {children}
      </MuiButton>
    );
  }

  return (
    <MuiButton
      ref={ref as Ref<HTMLButtonElement>}
      // `link` and `inline` are the façade's own variants: MUI has neither, so both ride on `text`
      // and the typography does the rest.
      variant={variantProp}
      color={colorProp}
      size={size}
      loading={loading}
      disabled={disabled}
      autoFocus={autoFocus}
      fullWidth={fullWidth}
      startIcon={startIcon}
      endIcon={endIcon}
      type={type}
      sx={sx}
      // Omit the handler when unset: exactOptionalPropertyTypes rejects onClick={undefined}.
      {...(onClick ? { onClick } : {})}
      {...aria}
    >
      {children}
    </MuiButton>
  );
});
